import { FieldError, useFieldArray, useFormContext } from "react-hook-form"
import { FormItem, FormControl, FormMessage, FormLabel } from "../ui/form"
import { Button } from "../ui/button"
import { SquareCheckBig, Plus, Trash2 } from "lucide-react"
import { WorkLogFormData } from "@pma/utils/work-diaries-schema"
import AssignToUserTask from "@pma/components/projects/assign-to-user-task"
import useTranslator from "@pma/hooks/use-translator"
import { RequiredFieldLabel } from "../ui/required-field-label"

export function TaskForm() {
  const { translate } = useTranslator()

  const {
    watch,
    setValue,
    getValues,
    register,
    formState: { errors },
    control,
  } = useFormContext<WorkLogFormData>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tasks",
  })

  return (
    <div className='space-y-8'>
      {/* Tasks Section with border */}
      <div className='space-y-4 rounded-lg border border-gray-200 p-4 shadow-sm'>
        <div className='flex items-center gap-2'>
          <SquareCheckBig className='h-5 w-5 text-emerald-500' />
          <h3 className='text-lg font-medium'>{translate("Tasks")}</h3>
        </div>

        <div className='space-y-3'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
            onClick={() =>
              append({ actionType: "add", description: "", assignedTo: [], completed: false })
            }
          >
            <Plus className='mr-2 h-4 w-4' />
            {translate("Add Task")}
          </Button>

          {fields.map((field, index) =>
            field.actionType === "delete" ? null : (
              <div
                key={field.id}
                className='grid grid-cols-1 items-center gap-3 rounded-md border border-gray-200 bg-white p-3 sm:grid-cols-[30px,1fr,1fr,40px] md:grid-cols-[30px,1fr,1fr,40px]'
              >
                <FormItem
                  error={errors.tasks?.[index]?.completed}
                  className='flex w-6 justify-center'
                >
                  <FormControl
                    type='checkbox'
                    {...register(`tasks.${index}.completed`)}
                    className='h-5 w-5'
                  />
                  <FormMessage />
                </FormItem>

                <FormItem error={errors.tasks?.[index]?.description as FieldError | undefined}>
                  <FormLabel>
                    {translate("Task Description")} <RequiredFieldLabel tooltipText={translate("Task description is required")} />
                  </FormLabel>
                  <FormControl
                    {...register(`tasks.${index}.description`)}
                    placeholder={translate("Task Description")}
                  />
                  <FormMessage />
                </FormItem>

                <FormItem error={errors.tasks?.[index]?.assignedTo as FieldError | undefined}>
                  <AssignToUserTask
                    taskId={getValues(`tasks.${index}.id`)}
                    responsible={watch(`tasks.${index}.assignedTo`) || []}
                    onSubmit={async (idArray) => {
                      setValue(`tasks.${index}.assignedTo`, idArray)
                    }}
                  />
                  <FormMessage />
                </FormItem>

                <div className='flex justify-center'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='mt-1'
                    onClick={() => {
                      const currentDetails = getValues("tasks")
                      const item = currentDetails && currentDetails[index]

                      if (item?.id && currentDetails) {
                        // Mark existing item for deletion
                        const updated = [...currentDetails]
                        updated[index] = { ...updated[index], actionType: "delete" }
                        setValue("tasks", updated)
                      } else {
                        // Remove newly added (unsaved) item
                        remove(index)
                      }
                    }}
                  >
                    <Trash2 className='h-4 w-4 text-red-500' />
                  </Button>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
